import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../../lib/utils.js";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md border border-transparent font-semibold leading-none whitespace-nowrap transition-[background-color,border-color,color,box-shadow] duration-150 ease-in-out px-5 py-2.5 text-[0.95rem] disabled:cursor-progress disabled:opacity-60",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border-primary not-disabled:hover:bg-primary-hover not-disabled:hover:border-primary-hover",
        accent:
          "bg-accent text-accent-contrast border-accent not-disabled:hover:bg-accent-hover not-disabled:hover:border-accent-hover",
        destructive:
          "bg-destructive text-destructive-foreground border-destructive not-disabled:hover:bg-destructive-hover not-disabled:hover:border-destructive-hover",
        outline:
          "bg-surface text-foreground border-border-strong not-disabled:hover:bg-surface-muted not-disabled:hover:border-subtle",
        ghost: "bg-transparent text-foreground not-disabled:hover:bg-surface-muted",
        link: "border-0 bg-transparent px-0 py-1 text-accent underline underline-offset-[0.15em] not-disabled:hover:text-accent-hover",
        whatsapp:
          "bg-whatsapp text-whatsapp-contrast border-whatsapp not-disabled:hover:bg-whatsapp-hover not-disabled:hover:border-whatsapp-hover",
      },
      size: {
        default: "",
        sm: "px-[0.85rem] py-[0.4rem] text-[0.85rem]",
        lg: "px-6 py-3 text-base",
      },
      linkDestructive: {
        true: "text-destructive not-disabled:hover:brightness-110",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      linkDestructive: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  linkDestructive?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, linkDestructive, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, linkDestructive }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
